import { HTMLSelect, InputGroup } from "@blueprintjs/core";
import { PaginationButtons } from "@focus/focusUI";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  getInputOrbitFilesPageResponse,
  type InputOrbitRow,
} from "../../services/inputs";
import type { ColumnFiltersState } from "../../services/helpers/pager";
import {
  getComputedInputState,
  getOrbitStatusDisplay,
  InputStateBadge,
} from "../../utils/inputsOrbits";
import { InfoField } from "../InfoField";
import { EmptyStateCard } from "../EmptyStateCard";
import {
  ActionsColumnItem,
  type TableActionType,
} from "../table/TableActionColumn";
import type { InputsStateType } from "../../types/inputs";
import { useDebounced } from "../../hooks/useDebounced";

type InputsFilterField =
  | "filename"
  | "organization"
  | "uploadedBy"
  | "status"
  | "linkedOEM.spaceObject.name.value";
type InputsSortBy = InputsFilterField | "createdAt" | "updatedAt";
type SortDirection = "asc" | "desc";

interface InputOrbitCardListProps {
  actions: TableActionType[];
  base: string;
  downloadData: (payload: { id: string }) => void;
  filter?: ColumnFiltersState;
  refreshKey?: string | number;
}

const INPUTS_FIELD_OPTIONS: { label: string; value: InputsFilterField }[] = [
  { label: "Filename", value: "filename" },
  { label: "Organization", value: "organization" },
  { label: "Uploaded by", value: "uploadedBy" },
  { label: "Status", value: "status" },
  { label: "Linked OEM name", value: "linkedOEM.spaceObject.name.value" },
];

const FILTER_FIELD_OPTIONS: { label: string; value: InputsFilterField }[] =
  INPUTS_FIELD_OPTIONS;

const SORT_OPTIONS: { label: string; value: InputsSortBy }[] = [
  { label: "Created at", value: "createdAt" },
  { label: "Updated at", value: "updatedAt" },
  ...INPUTS_FIELD_OPTIONS,
];

const SORT_DIRECTION_OPTIONS: Array<{ label: string; value: SortDirection }> = [
  { label: "Ascending", value: "asc" },
  { label: "Descending", value: "desc" },
];

const buildInputsSearchFilter = (
  field: InputsFilterField,
  value: string,
): ColumnFiltersState => {
  const query = value.trim();

  if (!query) {
    return [];
  }

  if (field === "status") {
    return [
      {
        id: field,
        operator: "equals",
        value: query.replaceAll(" ", "_").toUpperCase(),
      },
    ];
  }

  return [{ id: field, operator: "contains", value: query }];
};

const statusPillClassName: Record<string, string> = {
  "#5f6b7c": "bg-slate-100 text-slate-600 ring-slate-200",
  "#738091": "bg-slate-100 text-slate-600 ring-slate-200",
  "#2d72d2": "bg-blue-100 text-blue-700 ring-blue-200",
  "#238551": "bg-emerald-100 text-emerald-700 ring-emerald-200",
  "#c84144": "bg-red-100 text-red-700 ring-red-200",
};

const expirationCardClassName = {
  none: "border-slate-200 shadow-slate-100/70",
  warning: "border-amber-200 shadow-amber-100/70",
  active: "border-emerald-200 shadow-emerald-100/70",
  test: "border-blue-200 shadow-blue-100/70",
} as const;

const expirationAccentClassName = {
  none: "bg-slate-300",
  warning: "bg-amber-400",
  active: "bg-emerald-200",
  test: "bg-blue-200",
} as const;

const getExpirationTone = (state: InputsStateType | null) => {
  switch (state) {
    case "ACTIVE":
      return "active";
    case "ABOUT_TO_EXPIRE":
      return "warning";
    case "TEST":
      return "test";
    case "EXPIRED":
    default:
      return "none";
  }
};

const getDisplayText = (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    const wrapped = value as { value?: unknown; units?: unknown };

    if (
      wrapped.value === undefined ||
      wrapped.value === null ||
      wrapped.value === ""
    ) {
      return "-";
    }

    return wrapped.units
      ? `${wrapped.value} ${wrapped.units}`
      : `${wrapped.value}`;
  }

  return `${value}`;
};

export const InputOrbitCardList = ({
  actions,
  base,
  downloadData,
  filter,
  refreshKey,
}: InputOrbitCardListProps) => {
  const [rows, setRows] = useState<InputOrbitRow[]>([]);
  const [filterField, setFilterField] = useState<InputsFilterField>("filename");
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState<InputsSortBy>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const { cancel: cancelSearchDebounce, update: updateSearchDebounce } =
    useDebounced<string>({
      initialValue: "",
      onDebounce: setSearchText,
    });

  useEffect(() => cancelSearchDebounce, [cancelSearchDebounce]);

  const searchFilter = useMemo(
    () => buildInputsSearchFilter(filterField, searchText),
    [filterField, searchText],
  );

  const appliedFilter = useMemo(
    () => [...(filter ?? []), ...searchFilter],
    [filter, searchFilter],
  );

  const externalFilterKey = useMemo(
    () => JSON.stringify(filter ?? []),
    [filter],
  );

  useEffect(() => {
    setPageIndex(0);
  }, [externalFilterKey]);

  useEffect(() => {
    let cancelled = false;

    const loadRows = async () => {
      try {
        const page = await getInputOrbitFilesPageResponse({
          page: pageIndex,
          size: pageSize,
          filter: appliedFilter,
          sort: [
            {
              id: sortBy,
              desc: sortDirection === "desc",
            },
          ],
        });

        if (cancelled) {
          return;
        }

        const nextPageCount = Math.max(page.totalPages, 1);

        setRows(page.content);
        setTotalRows(page.totalElements);

        if (pageIndex > nextPageCount - 1) {
          setPageIndex(nextPageCount - 1);
        }
      } catch (error) {
        console.error(error);
      }
    };

    void loadRows();

    return () => {
      cancelled = true;
    };
  }, [appliedFilter, pageIndex, pageSize, refreshKey, sortBy, sortDirection]);

  const pageCount = Math.max(Math.ceil(totalRows / pageSize), 1);
  const currentPageRows = rows;
  const hasRows = totalRows > 0;
  const actionContext = useMemo(
    () => ({
      actions,
      base,
      downloadData,
    }),
    [actions, base, downloadData],
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-slate-50/50">
      <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
        <div className="focusoc-form-shell flex flex-wrap items-end gap-3">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Search
            </span>
            <InputGroup
              leftIcon="search"
              onChange={(event) => {
                const nextValue = event.target.value;
                setPageIndex(0);
                setSearchInput(nextValue);
                updateSearchDebounce(() => nextValue);
              }}
              placeholder="Enter a value for the selected field"
              type="search"
              value={searchInput}
            />
          </label>
          <label className="flex min-w-[180px] flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Filter by
            </span>
            <HTMLSelect
              aria-label="Filter input orbits by field"
              fill
              onChange={(event) => {
                setPageIndex(0);
                setFilterField(event.currentTarget.value as InputsFilterField);
              }}
              options={FILTER_FIELD_OPTIONS}
              value={filterField}
            />
          </label>

          <label className="flex min-w-[180px] flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Sort by
            </span>
            <HTMLSelect
              aria-label="Sort input orbits by field"
              fill
              onChange={(event) => {
                setPageIndex(0);
                setSortBy(event.currentTarget.value as InputsSortBy);
              }}
              options={SORT_OPTIONS}
              value={sortBy}
            />
          </label>

          <label className="flex min-w-[150px] flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase text-slate-500">
              Order
            </span>
            <HTMLSelect
              aria-label="Sort input orbits order"
              fill
              onChange={(event) => {
                setPageIndex(0);
                setSortDirection(event.currentTarget.value as SortDirection);
              }}
              options={SORT_DIRECTION_OPTIONS}
              value={sortDirection}
            />
          </label>

          <span className="mb-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
            {totalRows}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {currentPageRows.length === 0 ? (
          <EmptyStateCard
            icon="satellite"
            title={
              hasRows
                ? "No input orbits match the current filters"
                : "No input orbits to show"
            }
            description={
              hasRows
                ? "Adjust search or sorting to widen the list."
                : "Wait for input orbit data to become available."
            }
          />
        ) : (
          <div className="grid min-w-0 grid-cols-1 gap-3">
            {currentPageRows.map((row) => {
              const statusDisplay = getOrbitStatusDisplay(row);
              const inputState = getComputedInputState(row);
              const expirationTone = getExpirationTone(inputState);
              const data = { id: row.id, row: { original: row } };

              return (
                <article
                  className={`relative flex w-full min-w-0 flex-col overflow-hidden rounded-lg border bg-white p-3 shadow-sm ${expirationCardClassName[expirationTone]}`}
                  key={row.id}
                >
                  <span
                    className={`absolute inset-y-0 left-0 w-1 ${expirationAccentClassName[expirationTone]}`}
                  />

                  <header className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200/70 pb-2 pl-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ring-1 ring-inset ${
                          statusPillClassName[statusDisplay.color] ??
                          "bg-slate-100 text-slate-600 ring-slate-200"
                        }`}
                      >
                        {statusDisplay.title}
                      </span>
                      <InputStateBadge state={inputState} />
                      <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                        {row.filename}
                      </span>
                    </div>

                    <div className="flex items-center gap-1">
                      {actionContext.actions.map((action, index) =>
                        typeof action === "function" ? (
                          <span key={index}>{action(data, actionContext)}</span>
                        ) : (
                          <ActionsColumnItem
                            action={action}
                            context={actionContext}
                            data={data}
                            key={`${action}-${row.id}`}
                          />
                        ),
                      )}
                    </div>
                  </header>

                  <div className="flex w-full min-w-[760px] gap-2 overflow-x-auto pt-3 pb-1 lg:min-w-0 lg:overflow-visible">
                    <InfoField
                      fillHeight={false}
                      label="Organization"
                      strong
                      className="min-w-0 flex-1"
                      value={row.organization}
                    />
                    <InfoField
                      fillHeight={false}
                      label="Operator"
                      strong
                      className="min-w-0 flex-1"
                      value={row.operator}
                    />
                    <InfoField
                      fillHeight={false}
                      label="Uploaded by"
                      strong
                      className="min-w-0 flex-1"
                      value={row.uploadedBy}
                    />

                    <InfoField
                      fillHeight={false}
                      label="Created at"
                      className="min-w-0 flex-1"
                      value={row.createdAt}
                    />
                    <InfoField
                      fillHeight={false}
                      label="Updated at"
                      className="min-w-0 flex-1"
                      value={row.updatedAt}
                    />
                    <InfoField
                      fillHeight={false}
                      label="Linked OEM"
                      className="min-w-0 flex-1"
                      value={
                        row.linkedOEM?.id ? (
                          <Link
                            className="font-semibold text-slate-700 underline decoration-slate-300 underline-offset-2 hover:text-slate-950"
                            to={`/${base}/${row.linkedOEM.id}/info`}
                          >
                            {getDisplayText(
                              row.linkedOEM.spaceObject?.name ?? row.linkedOEM.id,
                            )}
                          </Link>
                        ) : (
                          "-"
                        )
                      }
                    />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {hasRows && (
        <div className="focusoc-table-pagination">
          <PaginationButtons
            pageIndex={pageIndex}
            pageCount={pageCount}
            canPreviousPage={pageIndex > 0}
            canNextPage={pageIndex < pageCount - 1}
            rowCountOnCurrentPage={currentPageRows.length}
            pageSize={pageSize}
            onFirstPage={() => setPageIndex(0)}
            onPreviousPage={() =>
              setPageIndex((currentPageIndex) =>
                Math.max(currentPageIndex - 1, 0),
              )
            }
            onNextPage={() =>
              setPageIndex((currentPageIndex) =>
                Math.min(currentPageIndex + 1, pageCount - 1),
              )
            }
            onLastPage={() => setPageIndex(pageCount - 1)}
            onPageIndexChange={setPageIndex}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPageIndex(0);
            }}
            paginationOptions={{
              disableGoToPage: false,
              disableRowsPerPage: false,
            }}
          />
        </div>
      )}
    </div>
  );
};
